import type { IconName } from '@garnet/ui';

/**
 * Metadata for the Garnet frontends shown on the landing page. Pure data — extracted
 * from the React component so it can be unit-tested directly and re-used by anything
 * that needs the platform's app catalogue.
 */

export type AppAccent = 'jade' | 'gold' | 'brick' | 'neutral';

export interface PortalApp {
  key: string;
  name: string;
  description: string;
  audience: string;
  icon: IconName;
  /** A single accent from the Garnet assurance spine; decorative, not semantic. */
  accent: AppAccent;
  /** Build-time override env var (Vite exposes any key prefixed with VITE_). */
  envVar: string;
  /** Default URL — matches the local-dev / docker-compose port layout. */
  defaultUrl: string;
}

/**
 * The five Garnet frontends, in platform-entry order. Defaults point at the canonical
 * Vite ports (5171–5175); override per environment with the matching VITE_* env var.
 */
export const APPS: readonly PortalApp[] = [
  {
    key: 'console',
    name: 'Console',
    description:
      'Procurement diligence workspace. Run the verification pipeline, review assurance, decide qualify / qualify-with-conditions / block on signed records.',
    audience: 'Buyers · Quality · Compliance',
    icon: 'grid',
    accent: 'jade',
    envVar: 'VITE_CONSOLE_URL',
    defaultUrl: 'http://localhost:5171',
  },
  {
    key: 'core-studio',
    name: 'Core Studio',
    description:
      'Authoring workbench for the Garnet Core rule store. Draft and cite rules, manage coverage, review change-driven revisit queues.',
    audience: 'Regulatory analysts · Rule authors',
    icon: 'book',
    accent: 'gold',
    envVar: 'VITE_CORE_STUDIO_URL',
    defaultUrl: 'http://localhost:5172',
  },
  {
    key: 'portal',
    name: 'Supplier Portal',
    description:
      'Counterparty-facing portal. Suppliers submit claims, upload documents, and respond to attestation requests.',
    audience: 'Suppliers · Counterparties',
    icon: 'exchange',
    accent: 'neutral',
    envVar: 'VITE_PORTAL_URL',
    defaultUrl: 'http://localhost:5173',
  },
  {
    key: 'inspector',
    name: 'Inspector',
    description:
      'Offline-first PWA for field and lab capture. Scan unit identifiers, capture evidence, and feed attestation runs back to the record.',
    audience: 'Inspectors · Lab technicians',
    icon: 'scan',
    accent: 'jade',
    envVar: 'VITE_INSPECTOR_URL',
    defaultUrl: 'http://localhost:5174',
  },
  {
    key: 'admin',
    name: 'Admin',
    description:
      'Internal operations. Source registry and health, support grants, webhook dead-letter replay, billing and customer ops.',
    audience: 'Garnet operators',
    icon: 'briefcase',
    accent: 'brick',
    envVar: 'VITE_ADMIN_URL',
    defaultUrl: 'http://localhost:5175',
  },
] as const;
